import {useEffect, useMemo, useRef, useState} from 'react';
import {Menu} from '@mui/material';
import {SVG} from '../../../constants/assetLists';
import moment from 'moment';
import {useNotifications} from '../../../hooks/useNotifications';
import {useNavigate} from 'react-router';
import AppButton from '../../common/AppButton';
import AppConfirmationDialog from '../../common/AppConfirmationDialog';

const renderNotificationContent = (item: any) => {
  switch (item.type) {
    case 'task':
    case 'announcement':
    case 'task_completed':
    case 'announcement_expired':
    case 'announcement_completed':
    case 'announcement_starting_soon':
      return (
        <>
          <div className="flex items-start gap-3">
            <img
              src={
                item.type === 'announcement_completed'
                  ? SVG.completedCheckboxIcon
                  : item.announcementIcon
              }
              className="w-5 h-5 mt-[2px]"
            />

            <span
              className={`text-[18px] font-semibold ${
                item.announcementType === 'Error'
                  ? 'text-[#E30B5C]'
                  : 'text-black'
              }`}>
              {item.announcementTitle}
            </span>
          </div>

          {(item.type === 'task' || item.type === 'task_completed') &&
            item.taskTitle && (
              <div className="flex items-start gap-3 pl-8">
                <img
                  src={
                    item.type === 'task_completed'
                      ? SVG.completedCheckboxIcon
                      : item.taskIcon!
                  }
                  className="w-4 h-4 mt-[2px]"
                />
                <span
                  className={`text-[18px] font-semibold ${
                    item.type === 'task_completed'
                      ? 'text-green-600 line-through'
                      : 'text-black'
                  }`}>
                  {item.taskTitle}
                </span>
              </div>
            )}
        </>
      );
    case 'form':
      return (
        <div className="flex items-start gap-3">
          <span className={`text-[18px] font-semibold text-black`}>
            {item.metadata.formTitle}
          </span>
        </div>
      );
    case 'regulation_before_date':
      return (
        <div className="flex items-start gap-3">
          <img src={SVG.timeIcon} className="w-5 h-5 mt-[2px]" />
          <span className={`text-[18px] font-semibold text-black`}>
            {item.title}
          </span>
        </div>
      );
    case 'daily_summary':
      return (
        <div className="flex items-start gap-3">
          <img
            src={SVG.daySummrayIcon}
            alt="daily summary"
            className="w-5 h-5 mt-[2px]"
          />
          <span className="text-[18px] font-semibold text-black">
            {item.title}
          </span>
        </div>
      );
    case 'checklist_completed':
    case 'checklist_due':
      return (
        <div className="flex items-start gap-3">
          <img
            src={item.type === 'checklist_completed' ? SVG.completedCheckboxIcon : SVG.clockIcon}
            alt="checklist completed"
            className="w-5 h-5 mt-[2px]"
          />
          <span className="text-[18px] font-semibold text-black">
            {item.checklistTitle || item.title}
          </span>
        </div>
      );
    case 'onboarding_branch_created':
      const branchLabel = item.subBranchName ? 'תת-הסניף' : 'הסניף';
      const branchName = item.subBranchName || item.branchName;

      return (
        <div className="flex items-start gap-3">
          <img
            src={SVG.settingBlackIcon}
            alt="onboarding branch created"
            className="w-5 h-5 mt-[2px]"
          />
          <span className="text-[18px] font-semibold text-black">
            {branchName
              ? `${branchLabel} "${branchName}" נוצר`
              : item.title || 'סניף חדש נוצר'}
          </span>
        </div>
      );
  }
};

const ITEM_HEIGHT = 120; // height of one notification item (approx)
const COLLAPSED_ITEMS = 3;
const COLLAPSED_HEIGHT = ITEM_HEIGHT * COLLAPSED_ITEMS; // 288px

function Notification() {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [limit, setLimit] = useState(10);
  const [isExpanded, setIsExpanded] = useState(false);

  const [notificationIdToDelete, setNotificationIdToDelete] = useState<
    string | null
  >(null);
  const [selectedNotificationIds, setSelectedNotificationIds] = useState<
    string[]
  >([]);
  const [showDeleteSelectedDialog, setShowDeleteSelectedDialog] =
    useState(false);

  const navigate = useNavigate();
  const open = Boolean(anchorEl);
  const isActive = Boolean(anchorEl);
  const {
    notifications,
    unreadCount: notificationCount,
    markAllAsRead,
    deleteNotification,
    notificationsData,
    deleteSelectedNotifications,
    isDeletingSelectedNotifications,
  } = useNotifications({
    page: 1,
    limit,
  });

  /* selected ids can outlive the notifications they point at (after a bulk
     delete, a refetch, or a notification removed somewhere else), so always
     work with the ones still present in the list */
  const selectedIds = useMemo(() => {
    if (selectedNotificationIds.length === 0) return [];

    const visibleIds = new Set(
      notifications.map((notification: any) => notification._id),
    );

    return selectedNotificationIds.filter(id => visibleIds.has(id));
  }, [selectedNotificationIds, notifications]);
  const stringifyUser = localStorage.getItem('user');
  const user =
    stringifyUser && stringifyUser !== 'undefined'
      ? JSON.parse(stringifyUser)
      : null;
  const userFullName = useMemo(() => {
    if (!user) return '';

    const firstName = (user.firstName ?? '').trim();
    const lastName = (user.lastName ?? '').trim();

    if (!firstName && !lastName) return '';

    return `${firstName}${firstName && lastName ? ' ' : ''}${lastName}`;
  }, [user]);

  const isDeleteDialogOpen =
    showDeleteSelectedDialog || !!notificationIdToDelete;
  const deleteDialogSubTitle =
    notificationIdToDelete || selectedIds.length === 1
      ? 'האם למחוק את ההתראה?'
      : `${selectedIds.length} התראות יימחקו, האם למחוק?`;

  const getAnnouncementIconByType = (announcementType?: string) => {
    switch (announcementType) {
      case 'Regular':
        return SVG.regularAnnouncementIcon;

      case 'Error':
        return SVG.redTriangleIcon;

      case 'Training':
        return SVG.tutorialsActiveIcon;

      case 'Administration':
        return SVG.settingBlackIcon;

      case 'Regulation':
        return SVG.administrationSmallIcon;

      case 'Notice':
        return SVG.exclamationCircleIcon;

      case 'Quick':
        return SVG.quickTypeIcon;

      case 'Reservation':
        return SVG.tableIcon;

      default:
        return SVG.regularAnnouncementIcon;
    }
  };
  const mapNotifications = (apiData: any[]) => {
    return apiData.map(item => {
      const isTask = item.type === 'task';
      const isTaskCompleted = item.type === 'task_completed';
      const announcementType = item?.metadata?.announcementType;
      const username = item?.metadata?.userName;

      return {
        id: item._id,
        type: item.type,
        announcementTitle:
          item.type === 'announcement'
            ? item.body
            : item.metadata?.announcementTitle,
        announcementId: item?.metadata?.announcementId,
        checklistId: item?.metadata?.checklistId,
        checklistTitle: item?.metadata?.checklistTitle,
        clientId: item?.metadata?.clientId,
        branchId: item?.metadata?.branchId,
        branchName: item?.metadata?.branchName,
        subBranchId: item?.metadata?.subBranchId,
        subBranchName: item?.metadata?.subBranchName,
        totalTasks: item?.metadata?.totalTasks ?? 0,
        announcementType,
        taskTitle: isTask || isTaskCompleted ? item.metadata?.taskTitle : null,
        metadata: item.metadata,
        date: item.createdAt
          ? moment(item.createdAt).local().format('HH:mm DD/MM/YYYY')
          : '',
        isRead: item.isRead,
        assignee: username ? `נפתחה ע״י: ${username}` : '',
        announcementIcon: getAnnouncementIconByType(announcementType),
        taskIcon: isTask || isTaskCompleted ? SVG.taskIcon : null,
        title: item?.title || item?.body,
        body: item?.body,
      };
    });
  };
  const handleExpand = () => {
    setIsExpanded(prev => !prev);
  };

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (anchorEl) {
      setAnchorEl(null);
    } else {
      setAnchorEl(e.currentTarget);
      if (notificationCount > 0) {
        markAllAsRead();
      }
    }
  };
  const handleClose = () => {
    setAnchorEl(null);
    setIsExpanded(false);
  };
  const mappedNotifications = mapNotifications(notifications);

  const clickTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(
    () => () => {
      if (clickTimeoutRef.current) clearTimeout(clickTimeoutRef.current);
    },
    [],
  );

  const handleNotificationPress = (item: any) => {
    if (clickTimeoutRef.current) clearTimeout(clickTimeoutRef.current);
    clickTimeoutRef.current = setTimeout(() => {
      setAnchorEl(null);
      switch (item.type) {
        case 'announcement':
        case 'task':
        case 'task_completed':
        case 'announcement_expired':
        case 'announcement_completed':
        case 'announcement_starting_soon':
          navigate(`/operations`, {
            state: {announcementId: item.announcementId},
          });
          break;
        case 'form':
          if (item.metadata?.isSubmitted) {
            return (window.location.href = `${window.location.origin}/forms`);
          }
          window.open(
            `${window.location.origin}/form/${item?.metadata?.formId}`,
            '_blank',
            'noopener,noreferrer',
          );

          break;
        case 'daily_summary':
          window.dispatchEvent(new Event('open-day-summary'));
          break;
        case 'checklist_completed':
        case 'checklist_due':
          if (item.checklistId) {
            navigate('/operations', {
              state: {checklistApprovalId: item.checklistId},
            });
          }
          break;
        case 'onboarding_branch_created':
          navigate(
            `/on-boarding${item.clientId ? `?clientId=${item.clientId}` : ''}`,
          );
          break;
        default:
          break;
      }
    }, 250);
  };

  const handleConfirmDeleteNotification = () => {
    if (!notificationIdToDelete) return;

    setSelectedNotificationIds(prev =>
      prev.filter(notificationId => notificationId !== notificationIdToDelete),
    );
    deleteNotification(notificationIdToDelete);
    setNotificationIdToDelete(null);
  };

  const handleDeleteSelectedNotifications = () => {
    if (selectedIds.length < 2 || isDeletingSelectedNotifications) return;

    setShowDeleteSelectedDialog(true);
    handleClose();
  };

  const handleToggleNotificationSelection = (id: string) => {
    if (clickTimeoutRef.current) clearTimeout(clickTimeoutRef.current);

    setSelectedNotificationIds(prev =>
      prev.includes(id)
        ? prev.filter(notificationId => notificationId !== id)
        : [...prev, id],
    );
  };

  const handleCloseDeleteDialog = () => {
    setNotificationIdToDelete(null);
    setShowDeleteSelectedDialog(false);
  };

  const handleConfirmDeleteDialog = () => {
    if (notificationIdToDelete) {
      handleConfirmDeleteNotification();
      return;
    }

    if (selectedIds.length === 0 || isDeletingSelectedNotifications) {
      handleCloseDeleteDialog();
      return;
    }

    setShowDeleteSelectedDialog(false);
    deleteSelectedNotifications(selectedIds, {
      onSuccess: () => setSelectedNotificationIds([]),
    });
  };

  return (
    <>
      {/* Notification Icon */}
      <div
        className={`relative flex items-center justify-center w-[52px] h-[44px] rounded-[16px] cursor-pointer transition-all
        ${
          isActive
            ? 'bg-gradient-to-b from-[#52FFE5] to-[#69EEFFFA]'
            : 'bg-transparent'
        }`}
        onClick={handleClick}>
        {/* 🔔 ICON */}
        <div className="w-[32px] h-[32px]">
          <img
            src={isActive ? SVG.ActiveAlert : SVG.InactiveAlert}
            alt="notification"
            className="w-full h-full object-contain"
          />
        </div>

        {/* 🔴 NOTIFICATION BADGE */}
        {notificationCount > 0 && (
          <span
            className="
      absolute
      bottom-0 left-5
      translate-x-1/2 -translate-y-1/2
      min-w-[18px] h-[18px]
      rounded-full
      bg-red-600 text-white
      text-[11px] font-semibold
      flex items-center justify-center
      px-[4px]
    ">
            {notificationCount}
          </span>
        )}
      </div>

      {/* Dropdown */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            mt: 1,
            borderRadius: '12px',
            minWidth: 420,
            p: 1,
            pt: 0,
            zIndex: 9999,
            transition: 'all 0.3s ease',
            overflow: 'hidden',
          },
        }}
        anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
        transformOrigin={{vertical: 'top', horizontal: 'left'}}>
        <div className="relative flex flex-col gap-2">
          <div className="flex justify-between items-center">
            <div className="flex items-start gap-2.5">
              <button
                // className="absolute right-2 top-0 w-5 h-5"
                onClick={handleExpand}>
                <img
                  src={isExpanded ? SVG.CollapseBlack : SVG.ExpandBlack}
                  alt="expand"
                  className={`w-5 h-5 transition-transform duration-200 `}
                />
              </button>

              {selectedIds.length > 1 && (
                <button
                  onClick={handleDeleteSelectedNotifications}
                  disabled={isDeletingSelectedNotifications}>
                  <img
                    src={SVG.listDeleteRedIcon}
                    alt="close"
                    className="w-5 h-5 object-contain"
                  />
                </button>
              )}
            </div>

            {/* Close dropdown */}
            <button
              // className="absolute left-2 top-2"
              onClick={() => {
                setAnchorEl(null);
                setIsExpanded(false);
              }}>
              <img src={SVG.closeDarkIcon} alt="close" className="w-2 h-2" />
            </button>
          </div>
          {/* Notifications container */}
          <div
            className="px-1"
            style={{
              maxHeight: isExpanded ? '90vh' : `${COLLAPSED_HEIGHT}px`,
              overflowY: 'auto',
              transition: 'max-height 0.35s ease',
            }}>
            <div
              className="
              flex flex-col
              rounded-lg
              border border-[#B9B9B9]
              pb-[30px]
            ">
              {mappedNotifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
                  <span className="text-[18px] font-semibold text-gray-700">
                    אין התראות זמינות
                  </span>
                  <span className="text-[15px] text-gray-500 mt-1">
                    התראות חדשות יופיעו כאן
                  </span>
                </div>
              ) : (
                mappedNotifications.map((item, index) => {
                  const isSelected = selectedIds.includes(item.id);

                  return (
                    <div
                      key={item.id}
                      className={`group relative flex gap-3 p-3 cursor-pointer 
                        ${index !== notifications.length - 1 ? 'border-b border-[#B9B9B9]' : ''} ${isSelected ? 'bg-[#EAF4FF]' : 'hover:bg-[#EAF4FF]'}`}
                      onClick={e => {
                        e.stopPropagation();
                        handleNotificationPress(item);
                      }}
                      onDoubleClick={e => {
                        e.stopPropagation();
                        handleToggleNotificationSelection(item.id);
                      }}>
                      {/* Hover close */}
                      <button
                        className="absolute left-2 top-2 hidden group-hover:block"
                        onClick={e => {
                          e.stopPropagation();
                          setNotificationIdToDelete(item.id);
                          handleClose();
                        }}>
                        <img
                          src={SVG.closeDarkIcon}
                          alt="close"
                          className="w-2 h-2"
                        />
                      </button>

                      {/* CONTENT */}
                      <div className="flex flex-col gap-2">
                        <div className="flex gap-3">
                          {isSelected && (
                            <img
                              src={SVG.checkTickIcon}
                              alt="selected"
                              className="w-7 h-7 object-contain"
                            />
                          )}
                          {renderNotificationContent(item)}
                        </div>
                        <div className="flex gap-2 text-[16px] text-grayText pl-8">
                          <span>{item.date}</span>
                          {item.type !== 'form' &&
                            item.type !== 'daily_summary' &&
                            item.type !== 'checklist_completed' &&
                            item.type !== 'checklist_due' &&
                            item.type !== 'onboarding_branch_created' && (
                              <>
                                <span>|</span>
                                <span>{`משימות: ${item.totalTasks}`}</span>
                              </>
                            )}
                        </div>

                        {item.type !== 'form' &&
                          item.type !== 'daily_summary' &&
                          item.type !== 'onboarding_branch_created' &&
                          item.assignee && (
                            <span className="text-[16px] text-grayText pl-8">
                              {item.assignee}
                            </span>
                          )}
                      </div>
                    </div>
                  );
                })
              )}
              {isExpanded && notificationsData?.data?.pagination?.hasNext && (
                <div
                  className="
                    sticky bottom-0
                    bg-white
                    border-t border-[#E5E5E5]
                    flex items-center justify-center
                    pt-4
                    pb-4
                    z-10
                  ">
                  <AppButton
                    variant="primary"
                    className="flex items-center justify-center w-1/2"
                    onClick={() => setLimit(prev => prev + 5)}>
                    Load More
                  </AppButton>
                </div>
              )}
            </div>
          </div>
        </div>
      </Menu>

      <AppConfirmationDialog
        open={isDeleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        icon={<img src={SVG.deleteRedIcon} className="w-[48px] h-[48px]" />}
        title="שימו לב"
        subTitle={deleteDialogSubTitle}
        confirmBtnText="כן"
        cancelBtnText="לא"
        onConfirm={handleConfirmDeleteDialog}
      />
    </>
  );
}

export default Notification;
